import { useCallback, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import useEvent from "react-use-event-hook";

import {
  REPORTS_DIAGRAM_PATH,
  REPORTS_TABLE_PATH,
  REPORTS_URL,
  ReportsPageViewTypeEnum,
  RoutePaths,
  usePrefixedRouting,
} from "@ryde/shared";

export const useReportsNavigation = () => {
  const location = useLocation();
  const { navigatePrefixed } = usePrefixedRouting();
  const { id } = useParams();

  const isReportsTableView = useMemo(
    (): boolean => location.pathname.includes(REPORTS_TABLE_PATH),
    [location.pathname],
  );

  const isReportsDiagramView = useMemo(
    (): boolean => location.pathname.includes(REPORTS_DIAGRAM_PATH),
    [location.pathname],
  );

  const isReportsRoute = useMemo(
    (): boolean => location.pathname.includes(REPORTS_URL),
    [location.pathname],
  );

  const navigateReportsTable = useCallback(
    (keepQuery = true): void => {
      const queryString =
        isReportsRoute && keepQuery ? location.search || "" : "";
      navigatePrefixed(
        `${REPORTS_URL}/${id}${REPORTS_TABLE_PATH}` + queryString,
      );
    },
    [id, isReportsRoute, location.search, navigatePrefixed],
  );

  const navigateReportsDiagram = useCallback(
    (keepQuery = true): void => {
      const queryString =
        isReportsRoute && keepQuery ? location.search || "" : "";
      navigatePrefixed(
        `${REPORTS_URL}/${id}${REPORTS_DIAGRAM_PATH}` + queryString,
      );
    },
    [id, isReportsRoute, location.search, navigatePrefixed],
  );

  const reportsPageViewType = useMemo(():
    ReportsPageViewTypeEnum | undefined => {
    if (isReportsTableView) {
      return ReportsPageViewTypeEnum.Table;
    }

    if (isReportsDiagramView) {
      return ReportsPageViewTypeEnum.Diagram;
    }

    return undefined;
  }, [isReportsTableView, isReportsDiagramView]);

  const navigateReportById = useEvent(
    (reportId: number, keepQuery = false): void => {
      const queryString = keepQuery ? location.search || "" : "";
      navigatePrefixed(`${RoutePaths.REPORTS}/${reportId}/table${queryString}`);
    },
  );

  return {
    isReportsTableView,
    isReportsDiagramView,
    isReportsRoute,
    navigateReportsTable,
    navigateReportsDiagram,
    reportsPageViewType,
    navigateReportById,
  };
};
